import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/components/ui/Toast';
import { useDailyCheckin, useWallet } from '@/features/games/rewards';
import { useAuth } from '@/lib/auth/AuthProvider';
import { dayKey, formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { errorKey } from '@/lib/utils/errors';

import { DailyStreakModal } from './DailyStreakModal';

const SHOWN_KEY = 'tashkeela.streakModalShown';

/**
 * Mounts the daily-streak popup once per day on app open (when the user hasn't
 * checked in yet). Renders nothing but the modal; safe to drop at the tabs root.
 */
export function DailyStreakGate() {
  const { userId } = useAuth();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const toast = useToast();
  const wallet = useWallet();
  const checkin = useDailyCheckin();
  const [visible, setVisible] = useState(false);
  const decided = useRef(false);

  const streak = wallet.data?.streak ?? 0;
  const checkedInToday = wallet.data?.checked_in_today ?? true;

  useEffect(() => {
    if (decided.current) return;
    if (!userId || !wallet.data) return;
    decided.current = true;
    if (checkedInToday) return;
    const today = dayKey(new Date());
    AsyncStorage.getItem(SHOWN_KEY)
      .then((last) => {
        if (last !== today) setVisible(true);
      })
      .catch(() => setVisible(true));
  }, [userId, wallet.data, checkedInToday]);

  const markShown = () => {
    AsyncStorage.setItem(SHOWN_KEY, dayKey(new Date())).catch(() => {});
  };

  const close = () => {
    markShown();
    setVisible(false);
  };

  const onClaim = () => {
    checkin.mutate(undefined, {
      onSuccess: (res) => {
        markShown();
        setVisible(false);
        if (!res.already_checked_in) {
          toast.show({
            message: t('rewards.claimed', {
              coins: formatNumber(res.reward, numeralOptions),
              streak: formatNumber(res.streak, numeralOptions),
            }),
            tone: 'success',
          });
        }
      },
      onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
    });
  };

  // Hide the moment the user is checked in (claimed here or from the Rewards tab).
  if (!visible || checkedInToday) return null;
  const cyclePos = streak % 14; // days already done this cycle (0..13)
  return (
    <DailyStreakModal
      visible={visible}
      claimDay={cyclePos + 1}
      filledBefore={cyclePos}
      reward={10 + Math.min(streak + 1, 7) * 5}
      loading={checkin.isPending}
      onClaim={onClaim}
      onClose={close}
    />
  );
}
